import { Route, Switch } from 'react-router-dom';
import React, { useEffect, useState, useMemo } from 'react';
import MeuConsumptionContext from '../../../contexts/MeuConsumptionContext';
import { useContentLocalizer } from '../../../hooks/useContentLocalizer';
import MeuConsumptionSummaryComponent from './components/MeuConsumptionSummary';
import { useTenant } from '../../../contexts/TenantContext';
import { useCommon } from '../../../contexts/CommonContext';
import MonthlyUsageComponent from './components/Monthly/MonthlyUsage';
import DailyUsageComponent from './components/Daily/DailyUsage';
import { AsideMenu } from '../components/AsideMenu';
import { useBillingConsumptionContext } from '../../../contexts/Billing/BillingConsumptionContext';
import { action, origin, REPORT_MEU_CONSUMPTION_OPEN, REPORT_MEU_CONSUMPTION_CLOSED } from '../../../shared/SegmentEvents';
import { useBillingPlanUsageContext } from '../../../contexts/Billing/BillingPlanUsageContext';
import { useBillingMetricsContext } from '../../../contexts/Billing/BillingMetricsContext';
import { usePageTracking } from '../hooks/usePageTracking';
import { MonthlyMeuConsumption } from '../../../models/billing/meuConsumption/MonthlyMeuConsumption';
import { sortByProperty } from '../../../utils/array';
import BillingMeuConsumptionApi from '../../../api/Billing/BillingMeuConsumptionApi';
import { billingRoutes, billingSubRoutes } from '../../../routes/BillingRoutes';
import { useMeuConsumptionContext } from '../../../contexts/Billing/MeuConsumptionContext';

const MeuConsumption: React.FC = () => {
  const { dateToSearch, setDateToSearch, periodOptions, currentMonth } = useBillingConsumptionContext();
  const { compiledMeuConsumptionDataState, consolidatedMeuApiRequisition, isMeuLoading, meuMetricIds, localizationMeu } = useMeuConsumptionContext();
  const { planStatus, getPlanStatus } = useBillingPlanUsageContext();
  const billingMeuConsumptionApi = new BillingMeuConsumptionApi();
  const { hasEnabledMetric } = useBillingMetricsContext();
  const { tenant } = useTenant();
  const [monthlyMeuConsumptionDataState, setMonthlyMeuConsumptionDataState] = useState<MonthlyMeuConsumption[]>([]);
  const [dailyMeuConsumptionDataState, setDailyMeuConsumptionDataState] = useState<MonthlyMeuConsumption[]>([]);

  const {
    setHeaderContent,
    common: { loggedUser },
  } = useCommon();

  const PAGE_NAME = 'Consumo MEU';
  usePageTracking(PAGE_NAME, REPORT_MEU_CONSUMPTION_OPEN, REPORT_MEU_CONSUMPTION_CLOSED, tenant, loggedUser, true);

  const content = useContentLocalizer(localizationMeu.pageLocalization);

  useEffect(() => {
    setHeaderContent({
      redirect: '/data-consumption',
      text: content.formatString(content.header, tenant.info.name),
    });
    return () => setHeaderContent({ redirect: '/' });
  }, []);

  const sortMeuConsumptionMonthlyUsage = (sort) => {
    const sortedChatbotsConsumption = sortByProperty(monthlyMeuConsumptionDataState, sort.property, sort.order);
    setMonthlyMeuConsumptionDataState([...sortedChatbotsConsumption]);
  };

  const sortMeuConsumptionDailyUsage = (sort) => {
    const sortedDailyUsageConsumption = sortByProperty(dailyMeuConsumptionDataState, sort.property, sort.order);
    setDailyMeuConsumptionDataState([...sortedDailyUsageConsumption]);
  };

  const trackingProps = {
    date: dateToSearch,
    planStatus: planStatus.current,
    origin: origin.MEUCONSUMPTION,
    action: action.SELECT,
  };

  const contextProps = useMemo(() => ({
    content,
    tenant,
    loggedUser,
    setHeaderContent,
    dateToSearch,
    setDateToSearch,
    periodOptions,
    currentMonth,
    trackingProps,
    planStatus,
    getPlanStatus,
    hasEnabledMetric,
    compiledMeuConsumptionDataState,
    isMeuLoading,
    consolidatedMeuApiRequisition,
    sortMeuConsumptionMonthlyUsage,
    sortMeuConsumptionDailyUsage,
    monthlyMeuConsumptionDataState,
    setMonthlyMeuConsumptionDataState,
    dailyMeuConsumptionDataState,
    setDailyMeuConsumptionDataState,
    billingMeuConsumptionApi,
    meuMetricIds,
  }), [
    content,
    tenant,
    loggedUser,
    setHeaderContent,
    dateToSearch,
    setDateToSearch,
    periodOptions,
    currentMonth,
    trackingProps,
    planStatus,
    getPlanStatus,
    hasEnabledMetric,
    compiledMeuConsumptionDataState,
    isMeuLoading,
    consolidatedMeuApiRequisition,
    sortMeuConsumptionMonthlyUsage,
    sortMeuConsumptionDailyUsage,
    monthlyMeuConsumptionDataState,
    dailyMeuConsumptionDataState,
    billingMeuConsumptionApi,
    meuMetricIds,
  ]);

  return (
    <MeuConsumptionContext.Provider value={contextProps}>
      <AsideMenu>
        <Switch>
          <Route exact path={billingRoutes.meuConsumption} component={MeuConsumptionSummaryComponent}></Route>
          <Route exact path={billingSubRoutes.meuConsumptionChatbots} component={MonthlyUsageComponent}></Route>
          <Route exact path={billingSubRoutes.meuConsumptionDaily} component={DailyUsageComponent}></Route>
        </Switch>
      </AsideMenu>
    </MeuConsumptionContext.Provider>
  );
};

export default MeuConsumption;
